/* ═══════════════════════════════════════════════════════════════════════════
   ALTUREX • Cart Drawer — Web Component propio sobre la AJAX Cart API.
   /cart.js · /cart/add.js · /cart/change.js · /cart/update.js
   Integración:
     · Intercepta el ícono del header (#cart-icon-bubble / [href="/cart"]).
     · Escucha los eventos que ya dispara el PDP custom: 'cart:open' y 'cart:refresh'.
     · Expone API compatible con product-form.js: open/close/renderContents/
       getSectionsToRender/setActiveElement (colección quick-add).
     · Actualiza el badge del header (.alturex-icon__badge) en tiempo real.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const CFG = window.alturexCartConfig || {};
  const THRESHOLD = CFG.threshold || 11099000; // $110.990 CLP, en "centavos" Shopify (×100)

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  // CLP sin decimales: 1999000 -> "$19.990"
  function money(cents) {
    const value = Math.round((cents || 0) / 100);
    return '$' + value.toLocaleString('es-CL');
  }

  function el(tag, cls, html) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (html != null) node.innerHTML = html;
    return node;
  }

  const ICON_CLOSE =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>';

  /* ── Badge del header (contador en tiempo real) ──────────────────────── */
  function updateHeaderBadge(count) {
    const link = document.querySelector('#cart-icon-bubble');
    if (!link) return;
    let badge = link.querySelector('.alturex-icon__badge');
    const n = parseInt(count, 10) || 0;
    if (n > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'alturex-icon__badge';
        link.appendChild(badge);
      }
      badge.textContent = n;
    } else if (badge) {
      badge.remove();
    }
  }
  window.updateCartCountBubble = updateHeaderBadge;

  /* ── Web Component ───────────────────────────────────────────────────── */
  class AlturexCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.panel = this.querySelector('.axd__panel');
      this.itemsEl = this.querySelector('[data-axd-items]');
      this.emptyEl = this.querySelector('[data-axd-empty]');
      this.shipEl = this.querySelector('[data-axd-ship]');
      this.promoEl = this.querySelector('[data-axd-promo]');
      this.promoNudgeEl = this.querySelector('[data-axd-promo-nudge]');
      this.subtotalEl = this.querySelector('[data-axd-subtotal]');
      this.countEl = this.querySelector('[data-axd-count]');
      this.footerEl = this.querySelector('[data-axd-footer]');
      this.crossEl = this.querySelector('[data-axd-cross]');
      this.errorEl = this.querySelector('[data-axd-error]');
      this.activeElement = null;
      this.busy = false;

      this.bindStaticEvents();
      this.interceptHeaderIcon();

      // Estado inicial desde el carrito real (sin abrir).
      this.getCart().then((cart) => { if (cart) this.render(cart); });

      window.alturexCart = this;
    }

    /* ---- Eventos estáticos (overlay, X, ESC, delegación, integración) ---- */
    bindStaticEvents() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-axd-close]')) { e.preventDefault(); this.close(); return; }

        const stepBtn = e.target.closest('[data-axd-step]');
        if (stepBtn) { this.onStep(stepBtn); return; }

        const removeBtn = e.target.closest('[data-axd-remove]');
        if (removeBtn) { this.onRemove(removeBtn); return; }

        const addBtn = e.target.closest('[data-axd-add]');
        if (addBtn) { this.onCrossAdd(addBtn); return; }

        const checkout = e.target.closest('[data-axd-checkout]');
        if (checkout) { e.preventDefault(); window.location.href = '/checkout'; return; }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });

      // Contrato del PDP custom (templates de producto). El PDP dispara cart:open
      // (sincrónico, el add aún no termina) y luego cart:refresh (add listo).
      // cart:open solo abre el shell con loading; cart:refresh trae el producto.
      document.documentElement.addEventListener('cart:open', () => {
        // El add aún no termina: marcamos espera y abrimos con loading.
        // El fetch real lo hará cart:refresh (cuando el producto ya está en el carrito).
        this._awaitingAdd = true;
        clearTimeout(this._awaitTimer);
        this._awaitTimer = setTimeout(() => { this._awaitingAdd = false; this.hideSyncing(); }, 6000);
        this._openShell();
        this.showSyncing();
      });
      window.addEventListener('cart:refresh', () => {
        this._awaitingAdd = false;
        clearTimeout(this._awaitTimer);
        this.refresh(true);
      });

      // Cambios en la página /cart (cart.js publica cartUpdate con item_count).
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
          const data = ev && ev.cartData;
          if (data && typeof data.item_count !== 'undefined') updateHeaderBadge(data.item_count);
        });
      }
    }

    interceptHeaderIcon() {
      const icon = document.querySelector('#cart-icon-bubble') || document.querySelector('[href="/cart"]');
      if (!icon) return;
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.addEventListener('click', (e) => { e.preventDefault(); this.open(icon); });
    }

    /* ---- Abrir / cerrar ---- */
    open(trigger, withLoading) {
      this._openShell(trigger);
      if (withLoading || this._awaitingAdd) this.showSyncing();
      // Si estamos esperando que termine un add (flujo del PDP), no hacemos fetch aquí:
      // cart:refresh traerá el carrito ya actualizado. En cualquier otro caso, refrescamos.
      if (!this._awaitingAdd) this.refresh();
    }

    // Abre solo la estructura (sin fetch). Usado por el evento cart:open del PDP,
    // donde el add aún no terminó: cart:refresh traerá el producto.
    _openShell(trigger) {
      if (trigger) this.activeElement = trigger;
      else if (!this.activeElement) this.activeElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('axd-locked');
      requestAnimationFrame(() => this.panel && this.panel.focus());
    }

    showSyncing() {
      if (this.panel) this.panel.classList.add('is-syncing');
      clearTimeout(this._syncTimer);
      this._syncTimer = setTimeout(() => this.hideSyncing(), 6000); // red de seguridad
    }
    hideSyncing() {
      clearTimeout(this._syncTimer);
      if (this.panel) this.panel.classList.remove('is-syncing');
    }

    close() {
      this.hideSyncing();
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('axd-locked');
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus();
      }
    }

    /* ---- API compatible con product-form.js (quick-add de colección) ---- */
    getSectionsToRender() { return []; }
    setActiveElement(node) { this.activeElement = node; }
    renderContents() { this.open(null, true); } // product-form ya hizo el add; abrimos con loading

    /* ---- AJAX Cart API ---- */
    getCart() {
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => r.json())
        .catch(() => null);
    }

    refresh(openAfter) {
      // Guard de secuencia: solo la última llamada puede pintar (evita que una
      // respuesta vieja —p.ej. el fetch pre-add— sobrescriba el carrito actualizado).
      const token = (this._refreshToken = (this._refreshToken || 0) + 1);
      return this.getCart().then((cart) => {
        if (token !== this._refreshToken) return cart; // respuesta fuera de orden: ignorar
        if (cart) this.render(cart);
        this.hideSyncing();
        if (openAfter && !this.classList.contains('is-open')) this._openShell();
        return cart;
      });
    }

    // Cambia la cantidad de una línea (line index 1-based). qty 0 = quitar.
    changeLine(line, qty) {
      if (this.busy) return Promise.resolve();
      this.setBusy(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: line, quantity: qty }),
      })
        .then((r) => { if (!r.ok) throw new Error('change'); return r.json(); })
        .then((cart) => {
          this.render(cart);
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(() => this.showError('No pudimos actualizar el carrito. Intenta de nuevo.'))
        .finally(() => this.setBusy(false));
    }

    addVariant(id, qty) {
      if (this.busy) return Promise.resolve(false);
      this.setBusy(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ id: parseInt(id, 10), quantity: qty || 1 },
          window.axPreventaEntrega ? { properties: { Entrega: window.axPreventaEntrega } } : {})),
      })
        .then((r) => {
          if (r.status === 422) return r.json().then((d) => { throw new Error(d.description || 'Sin stock'); });
          if (!r.ok) throw new Error('add');
          return r.json();
        })
        .then(() => this.refresh())
        .then(() => true)
        .catch((err) => {
          this.showError(err.message === 'add' ? 'No pudimos agregar el producto. Revisa tu conexión.' : err.message);
          return false;
        })
        .finally(() => this.setBusy(false));
    }

    /* ---- Handlers de UI ---- */
    onStep(btn) {
      const line = parseInt(btn.dataset.line, 10);
      const next = parseInt(btn.dataset.next, 10);
      const row = btn.closest('.axd__item');
      if (row) row.classList.add('is-busy');
      this.changeLine(line, next);
    }

    onRemove(btn) {
      const line = parseInt(btn.dataset.line, 10);
      const row = btn.closest('.axd__item');
      if (row) row.classList.add('is-busy');
      this.changeLine(line, 0);
    }

    onCrossAdd(btn) {
      const card = btn.closest('[data-axd-card]');
      const select = card ? card.querySelector('[data-axd-size]') : null;

      // Si el producto tiene tallas, exige elegir una.
      let id = btn.dataset.add;
      if (select) {
        id = select.value;
        if (!id) {
          select.classList.add('is-error');
          select.focus();
          this.showError('Elige tu talla para agregar el producto.');
          return;
        }
        select.classList.remove('is-error');
      }
      if (!id) return;

      const label = btn.textContent;
      btn.disabled = true;
      this.addVariant(id, 1).then((ok) => {
        if (ok) {
          btn.classList.add('is-done');
          btn.textContent = 'Agregado ✓';
          if (select) select.selectedIndex = 0;
          setTimeout(() => { btn.classList.remove('is-done'); btn.textContent = label; btn.disabled = false; }, 1600);
        } else {
          btn.disabled = false;
        }
      });
    }

    /* ---- Render ---- */
    render(cart) {
      const empty = !cart || cart.item_count === 0;

      updateHeaderBadge(cart ? cart.item_count : 0);
      if (this.countEl) this.countEl.textContent = cart ? cart.item_count : 0;
      if (this.countEl) this.countEl.hidden = empty;

      // Items
      if (this.itemsEl) {
        this.itemsEl.innerHTML = empty ? '' : cart.items.map((it, i) => this.itemHTML(it, i + 1)).join('');
        this.itemsEl.hidden = empty;
      }
      if (this.emptyEl) this.emptyEl.hidden = !empty;
      if (this.footerEl) this.footerEl.hidden = empty;
      if (this.shipEl) this.shipEl.hidden = empty;
      if (this.promoEl) this.promoEl.hidden = empty;

      // Subtotal
      if (this.subtotalEl && cart) this.subtotalEl.textContent = money(cart.total_price);
      if (cart) this.renderDescuento(cart, empty);

      // Barra de envío gratis
      if (!empty) this.renderShipping(cart.total_price);

      // Promo por volumen (10% x2 / 15% x3 pares)
      if (!empty) this.renderPromo(cart.items.reduce((s, it) => s + (/plantilla/i.test(it.product_title || it.title || '') ? 0 : it.quantity), 0));
      // Sugeridos: no repetir lo que ya está en el carrito (operador 09-10-2026)
      if (this.crossEl && cart) {
        const enCarro = new Set((cart.items || []).map((it) => (it.url || '').split('?')[0]));
        let quedan = 0;
        this.crossEl.querySelectorAll('[data-axd-card]').forEach((c) => {
          const a = c.querySelector('a[href]');
          const ocultar = !!a && enCarro.has(a.getAttribute('href').split('?')[0]);
          c.hidden = ocultar;
          if (!ocultar) quedan++;
        });
        this.crossEl.hidden = quedan === 0;
      }
    }

    // 09-10-2026 (operador): la franja muestra los dos tramos (2 pares -10% / 3 pares -15%) y marca el que ya tiene
    renderPromo(count) {
      const tramos = [[2, 10], [3, 15]];
      const lead = count <= 1 ? 'Lleva más, paga menos' : (count === 2 ? 'Suma 1 par más' : '¡Tienes el máximo!');
      if (this.promoEl) this.promoEl.classList.toggle('is-reached', count >= 2);
      if (this.promoNudgeEl) this.promoNudgeEl.innerHTML = '<span class="axd__tramo-lead">' + lead + '</span>' + tramos.map(([n, p]) =>
        '<span class="axd__tramo' + (count >= n ? ' is-on' : (n === Math.max(2, count + 1) ? ' is-next' : '')) + '">' + (count >= n ? '✓ ' : '') + n + '&nbsp;pares <b>−' + p + '%</b></span>').join('');
    }

    itemHTML(item, line) {
      const variant =
        item.options_with_values && item.options_with_values.length && !item.product_has_only_default_variant
          ? item.options_with_values.map((o) => `${o.name}: ${o.value}`).join(' / ')
          : (item.variant_title || '');
      // 09-10-2026: tachado = precio normal (antes de la preventa, window.axCompare) y píldora con el % de preventa
      const cmpU = (window.axCompare || {})[item.variant_id] || 0;
      const antes = cmpU > item.price ? cmpU * item.quantity : item.original_line_price;
      const compare =
        antes > item.final_line_price
          ? `<s>${money(antes)}</s>`
          : '';
      const preventa = cmpU > item.price ? `<span class="axd__preventa">Oferta −${Math.floor((cmpU - item.price) * 100 / cmpU)}%</span>` : '';
      const img = item.image
        ? `<img src="${item.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2')}" alt="${(item.product_title || '').replace(/"/g, '&quot;')}" loading="lazy" width="72" height="72">`
        : '';
      return `
        <div class="axd__item" data-line="${line}">
          <a class="axd__item-media" href="${item.url}">${img}</a>
          <div class="axd__item-main">
            <div class="axd__item-top">
              <a class="axd__item-name" href="${item.url}">${item.product_title || item.title}</a>
              <span class="axd__item-price">${money(item.final_line_price)}${compare}</span>
            </div>
            ${variant ? `<p class="axd__item-variant">${variant}</p>` : ''}${preventa}
            ${Object.keys(item.properties || {}).filter((k) => k[0] !== '_' && item.properties[k]).map((k) => `<p class="axd__item-variant axd__item-prop">${k}: ${String(item.properties[k]).replace(/</g, '&lt;')}</p>`).join('')}
            <div class="axd__item-bottom">
              <div class="axd__stepper" role="group" aria-label="Cantidad">
                <button class="axd__step" type="button" data-axd-step data-line="${line}" data-next="${item.quantity - 1}" aria-label="Quitar uno"${item.quantity <= 1 ? '' : ''}>−</button>
                <span class="axd__qty" aria-live="polite">${item.quantity}</span>
                <button class="axd__step" type="button" data-axd-step data-line="${line}" data-next="${item.quantity + 1}" aria-label="Agregar uno">+</button>
              </div>
              <button class="axd__remove" type="button" data-axd-remove data-line="${line}">Quitar</button>
            </div>
          </div>
        </div>`;
    }

    // 09-10-2026: subtotal (antes de descuentos), descuento (pares o código) y aviso de envío bajo el Total
    renderDescuento(cart, empty) {
      const sum = this.querySelector('[data-axd-sum]');
      const envio = this.querySelector('[data-axd-envio]');
      const desc = Math.max(0, (cart.original_total_price || 0) - (cart.total_price || 0));
      if (sum) {
        sum.hidden = empty || desc <= 0;
        if (!sum.hidden) {
          const pares = cart.items.reduce((a, it) => a + (/plantilla/i.test(it.product_title || it.title || '') ? 0 : it.quantity), 0);
          sum.querySelector('[data-axd-sub-label]').textContent = 'Subtotal (' + pares + (pares === 1 ? ' par)' : ' pares)');
          sum.querySelector('[data-axd-sub]').textContent = money(cart.original_total_price);
          const titulos = [];
          cart.items.forEach((it) => (it.line_level_discount_allocations || []).forEach((a) => titulos.push(a.discount_application && a.discount_application.title)));
          (cart.cart_level_discount_applications || []).forEach((a) => titulos.push(a.title));
          const t = titulos.filter(Boolean)[0] || '';
          const pct = (t.match(/(\d+)\s*%/) || [])[1];
          sum.querySelector('[data-axd-desc-label]').textContent = /AL COMPRAR/i.test(t) ? 'Descuento por pares' + (pct ? ' (' + pct + '%)' : '') : (t ? 'Descuento (' + t + ')' : 'Descuento');
          sum.querySelector('[data-axd-desc]').textContent = '−' + money(desc);
        }
      }
      // 09-10-2026 (operador): fuera "El envío se calcula al pagar"; esta línea dice cuánto ahorra y que es por la preventa
      if (envio) {
        const cmp = window.axCompare || {};
        const pre = empty ? 0 : cart.items.reduce((s, it) => s + Math.max(0, (cmp[it.variant_id] || 0) - it.price) * it.quantity, 0);
        const ahorro = pre + desc;
        envio.innerHTML = ahorro > 0 ? 'Ahorras <b>' + money(ahorro) + '</b>' + (pre > 0 ? (desc > 0 ? ' con oferta y pares' : ' con precio de oferta') : '') : '';
      }
    }

    renderShipping(total) {
      /* 09-10-2026 (operador): recorrido en una línea, sin título amarillo ni franjas */
      const reached = total >= THRESHOLD;
      const remaining = Math.max(0, THRESHOLD - total);
      const frac = Math.min(1, THRESHOLD ? total / THRESHOLD : 0);
      const msg = reached
        ? '<span class="axd__tl-ok">\u2713</span> Tienes envío gratis'
        : 'Faltan <b>' + money(remaining) + '</b> para envío gratis';
      const truck =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3.2v2.8h-7"/><circle cx="7" cy="17.5" r="1.8"/><circle cx="17" cy="17.5" r="1.8"/></svg>';
      this.shipEl.classList.toggle('is-reached', reached);
      this.shipEl.innerHTML =
        '<div class="axd__tl"><p class="axd__ship-msg">' + msg + '</p>' +
        '<div class="axd__tl-via" aria-hidden="true"><span class="axd__tl-ini"></span><span class="axd__tl-fill"></span><span class="axd__tl-fin">' + truck + '</span></div></div>';
      const fill = this.shipEl.querySelector('.axd__tl-fill');
      requestAnimationFrame(() => { fill.style.width = 'calc((100% - 16px) * ' + frac + ')'; });
    }

    /* ---- Estado / errores ---- */
    setBusy(state) {
      this.busy = state;
      if (this.panel) this.panel.classList.toggle('is-loading', state);
    }

    showError(message) {
      if (!this.errorEl) { window.alert(message); return; }
      this.errorEl.innerHTML = message;
      this.errorEl.hidden = false;
      requestAnimationFrame(() => this.errorEl.classList.add('is-visible'));
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => {
        this.errorEl.classList.remove('is-visible');
        setTimeout(() => { this.errorEl.hidden = true; }, 240);
      }, 3200);
    }
  }

  if (!customElements.get('alturex-cart-drawer')) {
    customElements.define('alturex-cart-drawer', AlturexCartDrawer);
  }
})();
/* AX-PIE-CORREO 25-09-2026, cambiado 09-10-2026: la oferta del pie es la misma de los pop-ups (envío gratis en la primera compra). Pide correo y WhatsApp (opcional, como el pop-up) con consentimiento para los dos -> Klaviyo lista UqAGDF -> Bienvenida UhwkXL, rama A (envío gratis, código por correo). No se manda oferta_popup: quien entra por el pie queda fuera de la prueba A/B. Si Klaviyo rechaza el cuerpo con WhatsApp, se reintenta solo con el correo. Borrar este bloque para quitarlo. */ (function(){var LEGAL='Al enviar aceptas recibir novedades y ofertas de ALTUREX por WhatsApp y correo. No es condición de compra. Te das de baja respondiendo STOP.';function fono(v){var d=(v||'').replace(/\D/g,'');if(!d)return '';if(d.length===9&&d.charAt(0)==='9')d='56'+d;if(d.length===11&&d.indexOf('569')===0)return '+'+d;return null}function enviar(cuerpo,rev){return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=WjEQE5',{method:'POST',headers:{'Content-Type':'application/json',revision:rev},body:JSON.stringify(cuerpo)}).then(function(r){if(!r.ok)throw 0})}function montar(){var cont=document.querySelector('.footer-premium .footer-newsletter-logo-social');if(!cont||document.getElementById('ax-pie-correo'))return;if(!document.getElementById('ax-pie-correo-css')){var s=document.createElement('style');s.id='ax-pie-correo-css';s.textContent='.ax-pie-correo{width:100%;max-width:420px;box-sizing:border-box;margin-left:auto;margin-right:auto}.ax-pie-correo *{box-sizing:border-box}.ax-pie-correo__fila input{width:100%!important;min-width:0!important;flex:1 1 0!important}.footer-premium .footer-block,.footer-premium .footer-newsletter-logo-social,.footer-premium .footer-social-bottom{min-width:0;max-width:100%}.ax-pie-correo__cta{display:block;width:100%;margin-top:6px;height:44px;padding:0 12px;border:0;border-radius:8px;background:#f0ac24;color:#0a0a0a;font:800 13px Montserrat,sans-serif;letter-spacing:.02em;cursor:pointer}.ax-pie-correo__cta[disabled]{opacity:.6;cursor:default}';document.head.appendChild(s)}var f=document.createElement('form');f.id='ax-pie-correo';f.className='ax-pie-correo';f.noValidate=true;f.innerHTML='<p class="ax-pie-correo__titulo">ENVÍO GRATIS EN TU PRIMERA COMPRA</p><p class="ax-pie-correo__sub">A todo Chile. Déjanos tu correo y WhatsApp y te mandamos tu código.</p><div class="ax-pie-correo__fila"><input type="email" name="email" required autocomplete="email" placeholder="Tu correo" aria-label="Tu correo"><input type="tel" name="fono" autocomplete="tel" inputmode="tel" placeholder="Tu WhatsApp (+56 9...)" aria-label="Tu WhatsApp"></div><button type="submit" class="ax-pie-correo__cta">QUIERO ENVÍO GRATIS</button><p class="ax-pie-correo__msg" role="status" aria-live="polite"></p><p class="ax-pie-correo__legal">'+LEGAL+'</p>';cont.appendChild(f);var msg=f.querySelector('.ax-pie-correo__msg'),btn=f.querySelector('button');function aviso(t,c){msg.textContent=t;msg.className='ax-pie-correo__msg'+(c?' '+c:'')}f.addEventListener('submit',function(e){e.preventDefault();var em=f.email.value.trim();if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)){aviso('Revisa tu correo, parece que tiene un error.','is-error');return}var tel=fono(f.fono.value);if(tel===null){aviso('Revisa tu WhatsApp: +56 9 y 8 dígitos.','is-error');return}btn.disabled=true;aviso('Enviando...');var subs={email:{marketing:{consent:'SUBSCRIBED'}}};var perfil={email:em,subscriptions:subs};if(tel){perfil.phone_number=tel;subs.whatsapp={marketing:{consent:'SUBSCRIBED'}}}var lista={list:{data:{type:'list',id:'UqAGDF'}}};enviar({data:{type:'subscription',attributes:{custom_source:'Pie de pagina alturex.cl',profile:{data:{type:'profile',attributes:perfil}}},relationships:lista}},'2026-07-15').catch(function(){return enviar({data:{type:'subscription',attributes:{custom_source:'Pie de pagina alturex.cl',profile:{data:{type:'profile',attributes:{email:em}}}},relationships:lista}},'2024-10-15')}).then(function(){aviso('¡Listo! Te mandamos tu código de envío gratis al correo.','is-ok');f.email.value='';f.fono.value=''}).catch(function(){aviso('No se pudo enviar. Intenta de nuevo en un momento.','is-error')}).then(function(){btn.disabled=false})})}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',montar);else montar()})(); /* FIN AX-PIE-CORREO */
/* ═══ 09-10-2026 (operador, 4ª): promo en una línea de un solo color, envío encima del total, descuento dentro del total */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax4)return;var P=C.prototype;P._ax4=1;
var render=P.render,desc=P.renderDescuento;
function m(c){return"$"+Math.round((c||0)/100).toLocaleString("es-CL")}
function pares(cart){return(cart.items||[]).reduce(function(s,it){return s+(/plantilla/i.test(it.product_title||it.title||"")?0:it.quantity)},0)}
P.render=function(cart){render.call(this,cart);var f=this.footerEl,s=this.shipEl;if(f&&s&&s.parentNode!==f)f.insertBefore(s,f.firstChild)};
P.renderPromo=function(n){if(!this.promoEl||!this.promoNudgeEl)return;this.promoEl.classList.toggle("is-reached",n>=2);
this.promoNudgeEl.innerHTML=n<=1?'<span class="axd__p4-g">Lleva</span> 2&nbsp;pares&nbsp;<b>−10%</b> <span class="axd__p4-g">y</span> 3&nbsp;pares&nbsp;<b>−15%</b>'
:n===2?'✓ <b>−10%</b> aplicado<span class="axd__p4-g">, suma 1&nbsp;par y pagas&nbsp;</span><b>−15%</b>'
:'✓ <b>−15%</b> aplicado <span class="axd__p4-g">por llevar '+n+'&nbsp;pares</span>'};
P.renderDescuento=function(cart,empty){desc.call(this,cart,empty);var sum=this.querySelector("[data-axd-sum]"),env=this.querySelector("[data-axd-envio]"),tot=this.querySelector(".axd__subtotal");if(sum)sum.hidden=!0;if(empty||!cart)return;
var d=Math.max(0,(cart.original_total_price||0)-(cart.total_price||0)),cmp=window.axCompare||{},pre=cart.items.reduce(function(s,it){return s+Math.max(0,(cmp[it.variant_id]||0)-it.price)*it.quantity},0),n=pares(cart);
var pct=Math.round(d*100/(cart.original_total_price||1));
if(tot){var a=tot.querySelector(".axd__antes");if(!a){a=document.createElement("s");a.className="axd__antes";tot.insertBefore(a,this.subtotalEl)}a.textContent=d>0?m(cart.original_total_price):"";a.hidden=!(d>0)}
if(env){var ah=pre+d,por=[];if(pre>0)por.push("oferta");if(d>0)por.push(n+"&nbsp;pares −"+pct+"%");env.innerHTML=ah>0?"Ahorras <b>"+m(ah)+"</b>"+(por.length?' <span class="axd__p4-g">por '+por.join(" + ")+"</span>":""):""}};
})();
/* ═══ 09-10-2026 (operador, 5ª): ítem del carrito más limpio (nombre corto, talla en minúscula, % de preventa junto al precio) */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax5)return;var P=C.prototype;P._ax5=1;var render=P.render;
P.render=function(cart){render.call(this,cart);if(!this.itemsEl)return;this.itemsEl.querySelectorAll(".axd__item").forEach(function(it){
var nm=it.querySelector(".axd__item-name");if(nm&&!nm.title){nm.title=nm.textContent.trim();nm.textContent=nm.title.replace(/\s*-\s*Zapatillas?\s+con\s+altura\s*$/i,"")}
var v=it.querySelector(".axd__item-variant");if(v)v.textContent=v.textContent.replace(/^\s*tallas?\s*:\s*/i,"Talla ");
var pv=it.querySelector(".axd__preventa"),pr=it.querySelector(".axd__item-price");if(pv&&pr&&!pr.querySelector(".axd__pct")){var p=(pv.textContent.match(/\d+\s*%/)||[""])[0];if(p){var e=document.createElement("small");e.className="axd__pct";e.textContent="Preventa −"+p.replace(/\s/g,"");pr.appendChild(e)}pv.remove()}})};
})();
/* ═══ COPIA COMPRIMIR v2 (09-10-2026): carrito más claro.
   - Franja de pares en una línea: qué pasa si suma otro par, sin "aplicado" ni puntos medios.
   - Cada par: nombre, y debajo en una fila +8 cm y talla corta; contenedor más bajo para que se vean los demás.
   - Precio con tachado (sin % en rojo).
   - Envío gratis: el del operador. Ahorro total en una frase.
   - Sugeridos: tarjetas que se deslizan de lado, con título según cuántos pares lleva.
   Va al final: se monta sobre las versiones 4ª y 5ª del operador sin borrarlas. */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax6)return;var P=C.prototype;P._ax6=1;
function m(c){return"$"+Math.round((c||0)/100).toLocaleString("es-CL")}
function esPar(it){return!/plantilla/i.test(it.product_title||it.title||"")}
function pares(cart){return(cart.items||[]).reduce(function(s,it){return s+(esPar(it)?it.quantity:0)},0)}
function pctPares(cart){var o=cart.original_total_price||0,d=Math.max(0,o-(cart.total_price||0));return o?Math.round(d*100/o):0}
function talla(txt){var s=String(txt||"").replace(/^\s*tallas?\s*:?\s*/i,"");var eu=(s.match(/^(\d+(?:[.,]\d)?)\s*EUR?/i)||[])[1];
  return eu?"Talla "+eu:(s?"Talla "+s:"")}
var FLECHA='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20V5M6 11l6-6 6 6"/></svg>';
var CHECK='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

var render=P.render;
P.render=function(cart){this._axCart=cart;render.call(this,cart);if(!cart||!cart.item_count)return;var self=this;
  /* Ítems: nombre, altura, talla y llegada a la izquierda del precio (llena el hueco bajo el nombre) */
  if(this.itemsEl)this.itemsEl.querySelectorAll(".axd__item").forEach(function(el){
    var it=cart.items[(+el.dataset.line||1)-1];if(!it)return;
    var top=el.querySelector(".axd__item-top"),nm=el.querySelector(".axd__item-name"),pr=el.querySelector(".axd__item-price");if(!top||!nm||!pr)return;
    var izq=document.createElement("div");izq.className="axd6-izq";top.insertBefore(izq,nm);izq.appendChild(nm);
    var meta=document.createElement("span");meta.className="axd6-meta";izq.appendChild(meta);
    var cm=(window.axAltura||{})[it.product_id];
    if(cm){var a=document.createElement("span");a.className="axd6-alt";a.innerHTML=FLECHA+"+"+cm+" cm";meta.appendChild(a)}
    var v=el.querySelector(".axd__item-variant:not(.axd__item-prop)");
    if(v){var t=document.createElement("span");t.className="axd6-talla";t.textContent=talla(v.textContent);meta.appendChild(t);v.remove()}
    /* sin "Llega el..." por par (se repetía; la fecha ya está arriba en el sitio) ni % en rojo (parecía algo malo): queda el tachado */
    var pc=pr.querySelector(".axd__pct");if(pc)pc.remove();
  });
  /* Envío gratis (diseño del operador): frase corta para que la línea quede larga (pedido 10-10); el camión al final dice que es el envío gratis */
  var sm=this.shipEl&&this.shipEl.querySelector(".axd__ship-msg"),T=(window.alturexCartConfig||{}).threshold||0;
  if(sm&&T){this.shipEl.setAttribute("aria-label",cart.total_price>=T?"Tienes envío gratis":"Faltan "+m(T-cart.total_price)+" para el envío gratis");sm.innerHTML=cart.total_price>=T?'<span class="axd__tl-ok">✓</span> Tienes envío gratis':"Faltan <b>"+m(T-cart.total_price)+"</b>"}
  /* Sugeridos: título según los pares, nombre corto, precio con punto y como máximo dos tarjetas */
  if(this.crossEl){var n=pares(cart),pct=pctPares(cart),tt=this.crossEl.querySelector(".axd__cross-title");
    if(tt)tt.textContent=n<=1?"Elige tu segundo par":n===2?"Elige tu tercer par":"Otros modelos";
    var vis=0;this.crossEl.querySelectorAll("[data-axd-card]").forEach(function(c){
      var nn=c.querySelector(".axd__card-name");if(nn&&!nn.dataset.ax6){nn.dataset.ax6=1;nn.title=nn.textContent.trim();nn.textContent=nn.title.split(" - ")[0].split("- ")[0].trim()}
      var pp=c.querySelector(".axd__card-price");if(pp)pp.textContent=pp.textContent.replace(/,/g,".");
      if(!c.hidden)vis++});
  }
};

/* Franja de pares: una sola línea con lo que pasa si suma otro par (pedido 10-10: nada de dos líneas) */
P.renderPromo=function(n){if(!this.promoEl||!this.promoNudgeEl)return;var cart=this._axCart||{},pct=pctPares(cart);
  this.promoEl.classList.add("axd6-promo");this.promoEl.classList.toggle("is-reached",n>=2);
  var frase=n<=1?"Suma otro par y ahorras <b>10% en los dos</b>"
    :n===2?(pct>0?"Suma 1 par más y pasas a <b>15% en los tres</b>":"Con 3 pares, <b>15% de descuento</b>")
    :(pct>0?"Tienes el descuento máximo: <b>15%</b>":"Llevas "+n+" pares");
  this.promoNudgeEl.innerHTML='<span class="axd6-pct-ico" aria-hidden="true">%</span><span class="axd6-frase">'+frase+"</span>"};

/* Ahorro en una frase, sin puntos medios */
var desc=P.renderDescuento;
P.renderDescuento=function(cart,empty){desc.call(this,cart,empty);var env=this.querySelector("[data-axd-envio]");if(!env||empty||!cart)return;
  var d=Math.max(0,(cart.original_total_price||0)-(cart.total_price||0)),cmp=window.axCompare||{},
  pre=cart.items.reduce(function(s,it){return s+Math.max(0,(cmp[it.variant_id]||0)-it.price)*it.quantity},0),n=pares(cart),pct=pctPares(cart),ah=pre+d;
  var por=pre>0&&d>0?" (oferta y "+pct+"% por pares)":pre>0?" con el precio de oferta":d>0?" con "+pct+"% por "+n+" pares":"";
  env.innerHTML=ah>0?"Ahorras <b>"+m(ah)+"</b>"+por:""};

/* Envío gratis: queda el del operador (frase + punto, línea y círculo con camión, verde al lograrlo), que prefiere el usuario */
})();
/* ═══ 16-10-2026 (preventa y carrito). Operador: "En el carrito no se entiende bien lo del upsell, el upsell de descuento
   deberia aparecer arriba de los pares a elegir, asi se entiende, ademas la linea de tiempo de envio gratis tampoco se
   entiende, no se entiende que es por envio gratis".
   - La franja de pares de arriba del todo se esconde y su frase pasa a ser EL título del bloque de sugeridos (no quedan
     dos títulos): 1 par -> "Suma tu 2º par: ahorra 10% en los dos + envío gratis" (lo mismo que la ficha, ax-segundo-par).
   - La barra dice "Te faltan $X para el envío gratis" / "✓ Tienes envío gratis".
   Por qué es verdad (conector Shopify, 16-10): envío gratis desde $110.990 en Santiago y regiones; el par más barato cuesta
   $82.990 -> 2 x 82.990 x 0,9 = $149.382 >= $110.990, así que con 2 pares siempre hay envío gratis. "10% OFF AL COMPRAR 2"
   y "15% OFF AL COMPRAR 3" combinan cualquiera de los 8 modelos. Si sube el umbral sobre $149.382, revisar la frase.
   Va al final: se monta sobre _ax4, _ax5 y _ax6 sin borrarlos. Para volver atrás, borrar este bloque. */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax7)return;var P=C.prototype;P._ax7=1;
function m(c){return"$"+Math.round((c||0)/100).toLocaleString("es-CL")}
function pares(cart){return(cart.items||[]).reduce(function(s,it){return s+(/plantilla/i.test(it.product_title||it.title||"")?0:it.quantity)},0)}
function pctPares(cart){var o=cart.original_total_price||0,d=Math.max(0,o-(cart.total_price||0));return o?Math.round(d*100/o):0}
P.renderPromo=function(){if(this.promoEl)this.promoEl.hidden=true};
var render=P.render;
P.render=function(cart){render.call(this,cart);if(this.promoEl)this.promoEl.hidden=true;if(!cart||!cart.item_count)return;
  var n=pares(cart),pct=pctPares(cart),T=(window.alturexCartConfig||{}).threshold||0;
  /* Barra: que diga para qué es */
  var sm=this.shipEl&&this.shipEl.querySelector(".axd__ship-msg");
  if(sm&&T){var ok=cart.total_price>=T;
    sm.innerHTML=ok?'<span class="axd__tl-ok">✓</span> Tienes envío gratis':"Te faltan <b>"+m(T-cart.total_price)+"</b> para el envío gratis";
    this.shipEl.setAttribute("aria-label",ok?"Tienes envío gratis":"Te faltan "+m(T-cart.total_price)+" para el envío gratis")}
  /* Título de los sugeridos = el aviso del descuento */
  var tt=this.crossEl&&this.crossEl.querySelector(".axd__cross-title");if(!tt)return;
  var frase=n<=0?"Lleva 2 pares: <b>ahorra 10%</b> + envío gratis"
    :n===1?"Suma tu 2º par: <b>ahorra 10% en los dos</b> + envío gratis"
    :n===2?(pct>0?"Suma tu 3º par: <b>ahorra 15% en los tres</b>":"Elige tu tercer par")
    :"Otros modelos";
  var promo=n<=2&&!(n===2&&pct<=0);
  tt.classList.toggle("axd7-tit",promo);
  tt.innerHTML=promo?'<span class="axd6-pct-ico" aria-hidden="true">%</span><span class="axd7-frase">'+frase+"</span>":frase;
};
})();

/* ═══ _ax8 · 16-10-2026 (operador: «Mejorable la linea de tiempo, se ve muy achicada»)
   La barra de envío pasa a ir debajo de la frase, de borde a borde. El avance se pasa como --axf (0 a 1) = total del carrito
   contra el umbral ($110.990); el CSS lo usa para el ancho del relleno, hasta el centro del camión. Va al final, sobre _ax7.
   Para volver atrás, borrar este bloque y las reglas «_ax8» del CSS. */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax8)return;var P=C.prototype;P._ax8=1;
var render=P.render;
P.render=function(cart){render.call(this,cart);var s=this.shipEl,T=(window.alturexCartConfig||{}).threshold||0;
  if(!s||!cart||!T)return;s.style.setProperty("--axf",Math.min(1,Math.max(0,(cart.total_price||0)/T)).toFixed(4))};
})();

/* ═══ _ax9 · 16-10-2026 (operador: «si uno apreta agregar sin elegir una talla, automaticamente se agrege un par con la talla
   que tiene el unico par que tenga en el carrito, o los pares siempre y cuando sean de la misma talla»)
   «+ Agregar» de los sugeridos sin talla elegida: si todos los pares del carrito son de una misma talla (EUR) y este modelo la
   tiene disponible, agrega esa talla. Si hay tallas distintas, si el modelo no la tiene o está agotada, pide la talla como antes.
   El selector sigue mostrando «Talla». Las plantillas no cuentan como par. Para volver atrás, borrar este bloque. */
(function(){var C=customElements.get("alturex-cart-drawer");if(!C||C.prototype._ax9)return;var P=C.prototype;P._ax9=1;
function eu(txt){var s=String(txt||"").replace(/^\s*tallas?\s*:?\s*/i,"").trim(),n=(s.match(/^(\d+(?:[.,]\d)?)\s*EUR?/i)||[])[1];return n?n.replace(",","."):s.toLowerCase()}
function tallaUnica(cart){var t=null,ok=true;((cart&&cart.items)||[]).forEach(function(it){if(!it.quantity||/plantilla/i.test(it.product_title||it.title||""))return;
  var o=(it.options_with_values||[])[0],v=eu(o?o.value:it.variant_title);if(!v)ok=false;else if(t===null)t=v;else if(t!==v)ok=false});return ok?t:null}
var orig=P.onCrossAdd;
P.onCrossAdd=function(btn){var card=btn.closest("[data-axd-card]"),sel=card&&card.querySelector("[data-axd-size]");
  if(!sel||sel.value)return orig.call(this,btn);
  var t=tallaUnica(this._axCart),op=t&&[].find.call(sel.options,function(o){return o.value&&!o.disabled&&eu(o.textContent)===t});
  if(!op)return orig.call(this,btn);
  sel.classList.remove("is-error");var label=btn.textContent;btn.disabled=true;
  this.addVariant(op.value,1).then(function(ok){if(!ok){btn.disabled=false;return}btn.classList.add("is-done");btn.textContent="Agregado ✓";
    setTimeout(function(){btn.classList.remove("is-done");btn.textContent=label;btn.disabled=false},1600)})};
})();
